/** Inline sign-in / create-account form, rendered directly in Settings. */

import { el, render } from '../utils/dom.js';
import { button, segmented } from './ui.js';
import { MIN_PASSWORD_LENGTH } from '../app/config.js';
import { emit, EVENTS } from '../app/eventBus.js';
import { signIn, signUp } from '../services/authService.js';

const MODES = [
  { id: 'signin', label: 'Sign in' },
  { id: 'signup', label: 'Create account' }
];

/** Fetch failures throw a generic TypeError with no useful message — reword them. */
function friendlyAuthError(err) {
  if (err instanceof TypeError || err?.message === 'Failed to fetch') {
    return "Couldn't reach the server. Check your connection and try again.";
  }
  return err?.message || 'Something went wrong. Try again.';
}

export function authPanel() {
  let mode = 'signin';
  let busy = false;
  let error = null;
  // Persisted across redraws — a busy/error state redraw must not clear what
  // the learner already typed.
  let email = '';
  let password = '';

  const container = el('div.stack');

  function draw() {
    const emailInput = el('input', {
      class: 'input input--text',
      type: 'email',
      autocomplete: 'email',
      placeholder: 'you@example.com',
      'aria-label': 'Email',
      value: email,
      disabled: busy,
      on: { input: (event) => { email = event.target.value; } }
    });
    const passwordInput = el('input', {
      class: 'input input--text',
      type: 'password',
      autocomplete: mode === 'signup' ? 'new-password' : 'current-password',
      placeholder: mode === 'signup' ? `At least ${MIN_PASSWORD_LENGTH} characters` : 'Password',
      'aria-label': 'Password',
      value: password,
      disabled: busy,
      on: {
        input: (event) => { password = event.target.value; },
        keydown: (event) => {
          if (event.key === 'Enter') submit();
        }
      }
    });

    async function submit() {
      const trimmedEmail = email.trim();
      if (!trimmedEmail || !password) {
        error = 'Enter an email and password.';
        draw();
        return;
      }
      if (mode === 'signup' && password.length < MIN_PASSWORD_LENGTH) {
        error = `Use at least ${MIN_PASSWORD_LENGTH} characters for your password.`;
        draw();
        return;
      }
      busy = true;
      error = null;
      draw();
      try {
        if (mode === 'signup') {
          const { session } = await signUp({ email: trimmedEmail, password });
          if (!session) {
            // Only happens if "Confirm email" was left on in Supabase: no email can be sent.
            throw new Error('Account created, but it needs email confirmation, which this site does not send. Please try again later.');
          }
          emit(EVENTS.toast, { title: 'Account created', body: 'You are signed in.', variant: 'success' });
          password = '';
        } else {
          await signIn({ email: trimmedEmail, password });
          emit(EVENTS.toast, { title: 'Signed in', variant: 'success' });
        }
      } catch (err) {
        error = friendlyAuthError(err);
      } finally {
        busy = false;
        draw();
      }
    }

    render(container, [
      segmented(MODES, mode, (id) => {
        mode = id;
        error = null;
        draw();
      }),
      el('div.stack', { style: { gap: '10px', marginTop: '12px', maxWidth: '360px' } }, [
        emailInput,
        passwordInput,
        error ? el('p.text-sm', { style: { color: 'var(--danger)' }, text: error }) : null,
        button(mode === 'signup' ? 'Create account' : 'Sign in', {
          variant: 'primary',
          disabled: busy,
          onClick: submit
        }),
        el('p.text-xs.text-subtle', {
          text:
            mode === 'signup'
              ? 'Your account stores your mastery scores, XP and streak — never the exact questions you were asked or how you answered them. No emails are sent, so there is no password reset: choose a password you will remember. Your progress always stays on this device too.'
              : 'Signing in restores your progress on this device, or lets you choose which to keep if this device already has some.'
        }),
        el('a.text-xs', { href: '#/privacy', text: 'Privacy: what is stored and how to delete it' })
      ])
    ]);
  }

  draw();
  return container;
}
